import React, { useCallback, useRef } from 'react';
import type { ComponentType, ForwardedRef, MouseEventHandler, ReactNode } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import ChartTrendUpIcon from '@atlaskit/icon/core/chart-trend-up';
import type { NewCoreIconProps } from '@atlaskit/icon/base-new';
import PeopleGroupIcon from '@atlaskit/icon/core/people-group';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import type { CustomPanel } from '../Feed.tsx';
import { FeedType } from '../useFeedTypeSelector.ts';
import { useFeedTypeSelectionAnalytics } from '../useFeedTypeSelectionAnalytics.ts';

const feedHeaderControlsContainerName = 'feed-header-controls';
const manyTabsTabCountThreshold = 5;

const i18n = defineMessages({
	following: {
		id: 'feed.card.categories.following',
		defaultMessage: 'Following',
		description:
			'Text that shows on a pill that allows you to switch between different content in the feed. this one shows content from places your following',
	},
	popular: {
		id: 'feed.card.categories.popular',
		defaultMessage: 'Popular',
		description:
			'Text that shows on a pill that allows you to switch between different content in the feed. this one shows content based on popularity/usage across the whole instance',
	},
});

export type FeedHeaderActionsRenderOptions = {
	shouldFitContainer: boolean;
	renderActionGroup: (children: ReactNode) => ReactNode;
	renderActionItem: (children: ReactNode, testId?: string) => ReactNode;
};

type FeedHeaderControlsProps = {
	renderPopularFeed: boolean;
	feedType: FeedType | string;
	setFeedType: (feedType: FeedType | string) => void;
	customPanels?: CustomPanel[];
	tabCount: number;
	popularFeedPillRef: React.RefObject<HTMLButtonElement>;
	followingFeedPillRef: React.RefObject<HTMLButtonElement>;
	title: ReactNode;
	hasActions: boolean;
	renderActions: (options: FeedHeaderActionsRenderOptions) => ReactNode;
};

/* eslint-disable @atlaskit/ui-styling-standard/no-container-queries -- Local named width-only container queries avoid ResizeObserver layout jitter. See HDS-4175. */
const styles = cssMap({
	titleHeader: {
		height: '32px',
	},
	selectorAndActions: {
		// @ts-expect-error -- containerName is valid CSS but not typed in cssMap. See HDS-4175.
		containerName: feedHeaderControlsContainerName,
		// @ts-expect-error -- containerType is valid CSS but not typed in cssMap. See HDS-4175.
		containerType: 'inline-size',
		width: '100%',
		whiteSpace: 'nowrap',
	},
	selectorContainer: {
		flex: '1 1 0',
		minWidth: '0',
		maxWidth: '100%',
		overflowX: 'auto',
	},
	actionsContainer: {
		maxWidth: '100%',
	},
	actionsContainerDefault: {
		// @ts-expect-error -- @container is valid CSS but not typed in cssMap. See HDS-4175.
		'@container feed-header-controls (max-width: 800px)': {
			width: '100%',
		},
	},
	actionsContainerManyTabs: {
		// @ts-expect-error -- @container is valid CSS but not typed in cssMap. See HDS-4175.
		'@container feed-header-controls (max-width: 960px)': {
			width: '100%',
		},
	},
	actionsGroup: {
		width: '100%',
	},
	actionsGroupDefault: {
		// @ts-expect-error -- @container is valid CSS but not typed in cssMap. See HDS-4175.
		'@container feed-header-controls (min-width: 800px)': {
			width: 'auto',
		},
	},
	actionsGroupManyTabs: {
		// @ts-expect-error -- @container is valid CSS but not typed in cssMap. See HDS-4175.
		'@container feed-header-controls (min-width: 960px)': {
			width: 'auto',
		},
	},
	actionItem: {
		minWidth: '0',
	},
	actionItemDefault: {
		// @ts-expect-error -- @container is valid CSS but not typed in cssMap. See HDS-4175.
		'@container feed-header-controls (max-width: 800px)': {
			flexBasis: '0',
			flexGrow: 1,
			flexShrink: 1,
		},
	},
	actionItemManyTabs: {
		// @ts-expect-error -- @container is valid CSS but not typed in cssMap. See HDS-4175.
		'@container feed-header-controls (max-width: 960px)': {
			flexBasis: '0',
			flexGrow: 1,
			flexShrink: 1,
		},
	},
	tabBar: {
		alignItems: 'center',
		boxSizing: 'border-box',
		width: 'fit-content',
		maxWidth: '100%',
		height: '32px',
		borderColor: token('color.border'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderRadius: token('radius.medium'),
		paddingInline: token('space.025'),
		overflowX: 'hidden',
	},
	tabBarDefault: {
		// @ts-expect-error -- @container is valid CSS but not typed in cssMap. See HDS-4175.
		'@container feed-header-controls (max-width: 800px)': {
			width: '100%',
			overflowX: 'hidden',
		},
	},
	tabBarManyTabs: {
		// @ts-expect-error -- @container is valid CSS but not typed in cssMap. See HDS-4175.
		'@container feed-header-controls (max-width: 960px)': {
			width: '100%',
			overflowX: 'hidden',
		},
	},
	tabButtonWrapper: {
		flex: '0 1 auto',
		minWidth: '0',
		overflow: 'hidden',
	},
	tabButtonWrapperDefault: {
		// @ts-expect-error -- @container is valid CSS but not typed in cssMap. See HDS-4175.
		'@container feed-header-controls (max-width: 800px)': {
			flexBasis: '0',
			flexGrow: 1,
			flexShrink: 1,
		},
	},
	tabButtonWrapperManyTabs: {
		// @ts-expect-error -- @container is valid CSS but not typed in cssMap. See HDS-4175.
		'@container feed-header-controls (max-width: 960px)': {
			flexBasis: '0',
			flexGrow: 1,
			flexShrink: 1,
		},
	},
	tabButtonContent: {
		alignItems: 'center',
		display: 'flex',
		gap: token('space.050'),
		justifyContent: 'center',
		minWidth: '0',
	},
	tabIconWrapper: {
		display: 'inline-flex',
		flexShrink: 0,
	},
	tabIconWrapperDefault: {
		// @ts-expect-error -- @container is valid CSS but not typed in cssMap. See HDS-4175.
		'@container feed-header-controls (max-width: 800px)': {
			display: 'none',
		},
	},
	tabIconWrapperManyTabs: {
		// @ts-expect-error -- @container is valid CSS but not typed in cssMap. See HDS-4175.
		'@container feed-header-controls (max-width: 960px)': {
			display: 'none',
		},
	},
	tabButtonLabel: {
		display: 'block',
		minWidth: '0',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
});
/* eslint-enable @atlaskit/ui-styling-standard/no-container-queries */

const defaultFeedHeaderActionsRenderOptions: FeedHeaderActionsRenderOptions = {
	shouldFitContainer: true,
	renderActionGroup: (children) => (
		<Inline
			space="space.100"
			grow="fill"
			xcss={cx(styles.actionsGroup, styles.actionsGroupDefault)}
		>
			{children}
		</Inline>
	),
	renderActionItem: (children, testId) => (
		<Box xcss={cx(styles.actionItem, styles.actionItemDefault)} testId={testId}>
			{children}
		</Box>
	),
};

const manyTabsFeedHeaderActionsRenderOptions: FeedHeaderActionsRenderOptions = {
	shouldFitContainer: true,
	renderActionGroup: (children) => (
		<Inline
			space="space.100"
			grow="fill"
			xcss={cx(styles.actionsGroup, styles.actionsGroupManyTabs)}
		>
			{children}
		</Inline>
	),
	renderActionItem: (children, testId) => (
		<Box xcss={cx(styles.actionItem, styles.actionItemManyTabs)} testId={testId}>
			{children}
		</Box>
	),
};

const assignForwardedRef = <TElement,>(ref: ForwardedRef<TElement>, value: TElement | null) => {
	if (typeof ref === 'function') {
		ref(value);
	} else if (ref) {
		ref.current = value;
	}
};

type TabButtonProps = {
	feedType: string;
	selected: boolean;
	Icon?: ComponentType<NewCoreIconProps>;
	children?: ReactNode;
	onClick: () => void;
	onMouseDown: MouseEventHandler<HTMLElement>;
	testId: string;
	buttonRef: ForwardedRef<HTMLButtonElement>;
	hasManyTabs: boolean;
};

type TabButtonTriggerProps = Omit<TabButtonProps, 'onClick' | 'onMouseDown'> & {
	onClick: MouseEventHandler<HTMLButtonElement>;
	onMouseDown: MouseEventHandler<HTMLButtonElement>;
	tooltipProps: Partial<React.ComponentProps<typeof Button>>;
	tooltipRef: ForwardedRef<HTMLElement>;
	labelRef: React.RefObject<HTMLSpanElement>;
};

const TabButtonTrigger = ({
	feedType,
	selected,
	Icon,
	children,
	onClick,
	onMouseDown,
	testId,
	buttonRef,
	hasManyTabs,
	tooltipProps,
	tooltipRef,
	labelRef,
}: TabButtonTriggerProps) => {
	const composedButtonRef = useCallback(
		(node: HTMLButtonElement | null) => {
			assignForwardedRef(tooltipRef, node);
			assignForwardedRef(buttonRef, node);
		},
		[buttonRef, tooltipRef],
	);

	return (
		<Button
			{...tooltipProps}
			appearance="subtle"
			spacing="compact"
			isSelected={selected}
			shouldFitContainer
			role="tab"
			id={`tab-${feedType.toLowerCase()}`}
			aria-selected={selected}
			onClick={onClick}
			onMouseDown={onMouseDown}
			testId={testId}
			ref={composedButtonRef}
		>
			<Box as="span" xcss={styles.tabButtonContent}>
				{Icon && (
					<Box
						as="span"
						xcss={cx(
							styles.tabIconWrapper,
							hasManyTabs ? styles.tabIconWrapperManyTabs : styles.tabIconWrapperDefault,
						)}
						testId={`${testId}-icon`}
					>
						<Icon label="" />
					</Box>
				)}
				<Box as="span" xcss={styles.tabButtonLabel} ref={labelRef} testId={`${testId}-label`}>
					{children}
				</Box>
			</Box>
		</Button>
	);
};

const TabButton = ({
	feedType,
	selected,
	Icon,
	children,
	onClick,
	onMouseDown,
	testId,
	buttonRef,
	hasManyTabs,
}: TabButtonProps) => {
	const labelRef = useRef<HTMLSpanElement>(null);

	const canShowTooltip = useCallback(() => {
		const label = labelRef.current;

		return Boolean(label && label.scrollWidth > label.clientWidth);
	}, []);

	return (
		<Box
			padding="space.025"
			xcss={cx(
				styles.tabButtonWrapper,
				hasManyTabs ? styles.tabButtonWrapperManyTabs : styles.tabButtonWrapperDefault,
			)}
		>
			<Tooltip content={children} isScreenReaderAnnouncementDisabled canAppear={canShowTooltip}>
				{({ onClick: onTooltipClick, onMouseDown: onTooltipMouseDown, ref, ...tooltipProps }) => (
					<TabButtonTrigger
						feedType={feedType}
						selected={selected}
						Icon={Icon}
						onClick={(event) => {
							onTooltipClick(event);
							onClick();
						}}
						onMouseDown={(event) => {
							onTooltipMouseDown(event);
							onMouseDown(event);
						}}
						testId={testId}
						buttonRef={buttonRef}
						hasManyTabs={hasManyTabs}
						tooltipProps={tooltipProps}
						tooltipRef={ref}
						labelRef={labelRef}
					>
						{children}
					</TabButtonTrigger>
				)}
			</Tooltip>
		</Box>
	);
};

type TabBarItemProps = {
	feedType: FeedType | string;
	selectedFeedType: FeedType | string;
	setFeedType: (newFeedType: FeedType | string) => void;
	Icon?: ComponentType<NewCoreIconProps>;
	children?: ReactNode;
	isPulseActive?: boolean;
	onPulseClick?: () => void;
	refObject?: React.RefObject<HTMLButtonElement>;
	hasManyTabs: boolean;
};

const TabBarItem = ({
	feedType,
	selectedFeedType,
	setFeedType,
	Icon,
	children,
	isPulseActive = false,
	onPulseClick = () => {},
	refObject,
	hasManyTabs,
}: TabBarItemProps) => {
	const { handleFeedTypeClick, handleMouseDown, handlePulseClick } = useFeedTypeSelectionAnalytics({
		feedType,
		selectedFeedType,
		setFeedType,
		isPulseActive,
		onPulseClick,
	});

	const handleTabBarClick = useCallback(() => {
		handlePulseClick();
		handleFeedTypeClick();
	}, [handleFeedTypeClick, handlePulseClick]);

	const selected = selectedFeedType === feedType;
	const testId = `${feedType.toLowerCase()}-feed-button`;

	return (
		<TabButton
			feedType={feedType}
			selected={selected}
			Icon={Icon}
			onClick={handleTabBarClick}
			onMouseDown={handleMouseDown}
			testId={testId}
			buttonRef={refObject ?? null}
			hasManyTabs={hasManyTabs}
		>
			{children}
		</TabButton>
	);
};

type TabBarProps = {
	selectedFeedType: FeedType | string;
	setSelectedFeedType: (newFeedType: FeedType | string) => void;
	customPanels?: CustomPanel[];
	renderPopularFeed: boolean;
	popularFeedPillRef: React.RefObject<HTMLButtonElement>;
	followingFeedPillRef: React.RefObject<HTMLButtonElement>;
	hasManyTabs: boolean;
};

const TabBar = ({
	selectedFeedType,
	setSelectedFeedType,
	customPanels,
	renderPopularFeed,
	popularFeedPillRef,
	followingFeedPillRef,
	hasManyTabs,
}: TabBarProps) => (
	<Flex
		xcss={cx(styles.tabBar, hasManyTabs ? styles.tabBarManyTabs : styles.tabBarDefault)}
		wrap="nowrap"
		role="tablist"
		aria-labelledby="tablist-feedtypes"
	>
		<TabBarItem
			feedType={FeedType.FOLLOWING}
			selectedFeedType={selectedFeedType}
			setFeedType={setSelectedFeedType}
			Icon={PeopleGroupIcon}
			refObject={followingFeedPillRef}
			hasManyTabs={hasManyTabs}
		>
			<FormattedMessage {...i18n.following} />
		</TabBarItem>
		{renderPopularFeed && (
			<TabBarItem
				feedType={FeedType.POPULAR}
				selectedFeedType={selectedFeedType}
				setFeedType={setSelectedFeedType}
				Icon={ChartTrendUpIcon}
				refObject={popularFeedPillRef}
				hasManyTabs={hasManyTabs}
			>
				<FormattedMessage {...i18n.popular} />
			</TabBarItem>
		)}
		{customPanels?.map((item) => (
			<TabBarItem
				key={item.key}
				feedType={item.key}
				selectedFeedType={selectedFeedType}
				setFeedType={setSelectedFeedType}
				Icon={item.icon}
				refObject={item.ref}
				isPulseActive={item.isPulseActive}
				onPulseClick={item.onPulseClick}
				hasManyTabs={hasManyTabs}
			>
				{item.title}
			</TabBarItem>
		))}
	</Flex>
);

export const FeedHeaderControls = ({
	title,
	renderPopularFeed,
	feedType,
	setFeedType,
	customPanels,
	tabCount,
	popularFeedPillRef,
	followingFeedPillRef,
	hasActions,
	renderActions,
}: FeedHeaderControlsProps) => {
	const hasManyTabs = tabCount >= manyTabsTabCountThreshold;
	const shouldRenderTabBar = renderPopularFeed || Boolean(customPanels?.length);
	const actionRenderOptions = hasManyTabs
		? manyTabsFeedHeaderActionsRenderOptions
		: defaultFeedHeaderActionsRenderOptions;
	const actions = hasActions ? renderActions(actionRenderOptions) : null;

	return (
		<Stack space="space.100">
			<Inline
				alignBlock="center"
				spread="space-between"
				space="space.100"
				xcss={styles.titleHeader}
			>
				<Heading size="xsmall" as="h2" id="tablist-feedtypes">
					{title}
				</Heading>
			</Inline>
			<Inline
				alignBlock="center"
				spread="space-between"
				space="space.100"
				rowSpace="space.100"
				shouldWrap
				xcss={styles.selectorAndActions}
			>
				{shouldRenderTabBar && (
					<Box xcss={styles.selectorContainer}>
						<TabBar
							selectedFeedType={feedType}
							setSelectedFeedType={setFeedType}
							customPanels={customPanels}
							renderPopularFeed={renderPopularFeed}
							popularFeedPillRef={popularFeedPillRef}
							followingFeedPillRef={followingFeedPillRef}
							hasManyTabs={hasManyTabs}
						/>
					</Box>
				)}
				{actions && (
					<Inline
						alignInline="end"
						xcss={cx(
							styles.actionsContainer,
							hasManyTabs ? styles.actionsContainerManyTabs : styles.actionsContainerDefault,
						)}
					>
						{actions}
					</Inline>
				)}
			</Inline>
		</Stack>
	);
};
